/**
 * AuthProvider - Keycloak (OIDC) Authentication Provider
 *
 * Wraps the application with react-oidc-context's authentication context.
 * Handles the sign-in redirect round-trip and provides auth state to
 * children via the unchanged useAuth() contract (../hooks/useAuth.js).
 */

import React, { useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthProvider as OidcAuthProvider, useAuth as useOidcAuth, hasAuthParams } from 'react-oidc-context';
import { oidcConfig, buildSigninRedirectArgs } from '../config/authConfig';
import { useAuth } from '../hooks/useAuth';
import { usePermissions } from '../hooks/usePermissions';
import LoadingScreen from './LoadingScreen';
import UnprofiledScreen from './UnprofiledScreen';
import Logo from './Logo';
import { useStorageAuth } from '../services/storageContext';

/**
 * The query parameters of an OIDC authorization RESPONSE (RFC 6749 §4.1.2 and
 * §4.1.2.1, plus Keycloak's `session_state` and RFC 9207's `iss`).
 */
const AUTH_RESPONSE_PARAMS = ['code', 'state', 'session_state', 'iss', 'error', 'error_description', 'error_uri'];

/**
 * The current URL with the authorization response removed.
 *
 * ONLY for a callback that has already failed. It is deliberately not part
 * of authConfig.js#buildSigninRedirectArgs: `/cli-auth` carries a `state`
 * parameter of its own, and stripping it there would break `tct auth login`.
 *
 * @returns {string} pathname plus whatever query survives
 */
const urlWithoutAuthResponse = () => {
  const params = new URLSearchParams(window.location.search);
  AUTH_RESPONSE_PARAMS.forEach((name) => params.delete(name));
  const query = params.toString();
  return query ? `${window.location.pathname}?${query}` : window.location.pathname;
};

/**
 * Where a retry should come back to.
 *
 * When the identity provider ANSWERS with an error, oidc-client-ts still
 * matches the response to the request that started it, and its
 * `ErrorResponse.state` is the `state` that request was given -- ours, from
 * authConfig.js#buildSigninRedirectArgs. Reusing its `returnTo` is what lets
 * a `tct auth login` that was cancelled once and retried land back on
 * `/cli-auth` rather than on "/" with the CLI still waiting at its listener.
 *
 * The value is read back from session storage, so it is checked for shape
 * like any other input: a same-origin path, nothing else. A response that
 * matched no request (replayed, bookmarked) carries no state at all.
 *
 * @param {{innerError?: {state?: {returnTo?: string}}}} error
 * @returns {string}
 */
const returnToAfterFailedSignin = (error) => {
  const original = error?.innerError?.state?.returnTo;
  const isSameOriginPath =
    typeof original === 'string' && original.startsWith('/') && !original.startsWith('//');
  return isSameOriginPath ? original : urlWithoutAuthResponse();
};

/**
 * Auth content wrapper that handles authentication states
 */
const AuthContent = ({ children }) => {
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const oidcAuth = useOidcAuth();
  const navigate = useNavigate();

  // react-oidc-context tags an error with its `source`. "signinCallback" is
  // everything its start-up effect catches: `signinCallback()` itself, our
  // `onSigninCallback`, and the `getUser()` that follows. A failed silent
  // renewal or a failed signinRedirect carries another source and keeps the
  // behaviour it had.
  const signinCallbackError =
    oidcAuth?.error?.source === 'signinCallback' ? oidcAuth.error : null;

  // When `signinCallback()` rejects, react-oidc-context dispatches the error
  // and leaves the URL alone (verified against 3.3.1). The response would
  // then be re-processed, and fail again, on every reload -- so remove it.
  useEffect(() => {
    if (signinCallbackError && hasAuthParams()) {
      navigate(urlWithoutAuthResponse(), { replace: true });
    }
  }, [signinCallbackError, navigate]);

  // A failed callback is an ERROR, not a loading state. This used to be
  // gated on the URL still holding `code`/`error`, which a failed callback
  // never clears, so a user who cancelled at the identity provider sat on
  // "Processing authentication..." until they edited the address bar.
  if (signinCallbackError && !isAuthenticated) {
    return <SigninFailedScreen error={signinCallbackError} />;
  }

  // `isLoading` does NOT cover the whole of a successful callback.
  // oidc-client-ts fires its user-loaded event from inside `signinCallback()`
  // (`_buildUser` awaits `_events.load`), which react-oidc-context turns into
  // `isLoading: false, isAuthenticated: true` BEFORE `onSigninCallback` has
  // navigated the response out of the URL. So an unprocessed response in the
  // URL still means "wait" -- but only while nothing has failed, which is the
  // half the old gate was missing. `hasAuthParams` is the library's own test
  // (it requires `state`), so `?error=x` on its own no longer spins either.
  const isProcessingRedirect = hasAuthParams() && !signinCallbackError;
  if (authLoading || isProcessingRedirect) {
    const message = hasAuthParams()
      ? "Processing authentication..."
      : "Checking authentication...";
    return <LoadingScreen message={message} />;
  }

  // If not authenticated, show login screen
  if (!isAuthenticated) {
    return <LoginScreen />;
  }

  // User is authenticated, now check permissions
  return <AuthenticatedContent>{children}</AuthenticatedContent>;
};

/**
 * Content for authenticated users - handles profile loading and permissions
 */
const AuthenticatedContent = ({ children }) => {
  const { getAccessToken } = useAuth();
  const { isLoading: permissionsLoading, isUnprofiled, error, profile } = usePermissions();
  const updateStorageAuth = useStorageAuth();

  // Inject auth token getter into storage service factory
  React.useEffect(() => {
    if (getAccessToken) {
      updateStorageAuth(getAccessToken);
    }
  }, [getAccessToken, updateStorageAuth]);

  // Show loading screen while loading user profile
  if (permissionsLoading) {
    return <LoadingScreen message="Loading user profile..." />;
  }

  // Debug: Log profile state
  console.log('AuthenticatedContent - Profile state:', {
    isLoading: permissionsLoading,
    isUnprofiled,
    error,
    hasProfile: !!profile,
    profileRoles: profile?.roles
  });

  // Show error if permissions check failed
  if (error) {
    // If the error is "Authentication required", redirect to login
    if (error === 'Authentication required') {
      console.log('AuthenticatedContent: Authentication required error detected, showing login screen');
      return <LoginScreen />;
    }

    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md w-full bg-white shadow-lg rounded-lg p-6">
          <div className="text-center">
            <div className="text-red-500 text-6xl mb-4">⚠️</div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              Authentication Error
            </h2>
            <p className="text-gray-600 mb-4">
              {error}
            </p>
            <button
              onClick={() => window.location.reload()}
              className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Show unprofiled screen for users awaiting approval
  if (isUnprofiled) {
    return <UnprofiledScreen />;
  }

  return children;
};

/**
 * Main AuthProvider component
 */
const AuthProvider = ({ children }) => {
  const navigate = useNavigate();

  const onSigninCallback = useCallback((user) => {
    // Restore the URL that initiated login and strip the auth response
    // (code/state) from the query string. This is the oidc-client-ts
    // equivalent of MSAL's `navigateToLoginRequestUrl: true` — see
    // authConfig.js#buildSigninRedirectArgs for how `returnTo` got here.
    // /cli-auth's single-use challenge parameters depend on landing back
    // here rather than at "/".
    const returnTo = user?.state?.returnTo || '/';
    navigate(returnTo, { replace: true });
  }, [navigate]);

  return (
    <OidcAuthProvider {...oidcConfig} onSigninCallback={onSigninCallback}>
      <AuthContent>
        {children}
      </AuthContent>
    </OidcAuthProvider>
  );
};

/**
 * Shown when the redirect back from the identity provider could not be
 * completed: the user cancelled or was denied, the response was replayed
 * (back button, bookmark, reload), or the state that started it is gone.
 *
 * The provider's own `error_description` is NOT rendered. It arrives through
 * the URL, so it is untrusted input; it goes to the console for support and
 * the screen shows wording this application chose.
 */
const SigninFailedScreen = ({ error }) => {
  const auth = useOidcAuth();
  const wasDenied = error?.innerError?.error === 'access_denied';

  useEffect(() => {
    console.error('Sign-in callback failed:', error?.message);
  }, [error]);

  const handleRetry = () => {
    // Never buildSigninRedirectArgs(): it reads the current URL, which may
    // still hold the failed response, and would carry it back as `returnTo`.
    auth.signinRedirect({ state: { returnTo: returnToAfterFailedSignin(error) } });
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="max-w-md w-full bg-white shadow-lg rounded-lg p-6">
        <div className="text-center" role="alert">
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            Sign-in was not completed
          </h2>
          <p className="text-gray-600 mb-4">
            {wasDenied
              ? 'The sign-in was cancelled or access was denied at the identity provider.'
              : 'Sign-in could not be completed. This can happen when a sign-in link is reused, bookmarked, or opened after the session that started it has ended, or when the identity provider could not be reached.'}
          </p>
          <button
            onClick={handleRetry}
            className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg"
          >
            Sign in again
          </button>
        </div>
      </div>
    </div>
  );
};

/**
 * Login screen for unauthenticated users
 */
const LoginScreen = () => {
  const auth = useOidcAuth();

  const handleLogin = () => {
    auth.signinRedirect(buildSigninRedirectArgs());
  };

  return (
    <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'var(--tct-blue-dark)' }}>
      <div className="max-w-md w-full bg-white shadow-xl rounded-lg p-8">
        <div className="text-center">
          {/* Logo */}
          <div className="mb-8">
            <Logo size="lg" variant="horizontal" noLink className="justify-center" />
            <p className="mt-4 text-2xl font-semibold text-gray-700">
              TCT AI
            </p>
          </div>

          {/* Login form */}
          <div className="space-y-6">
            <button
              onClick={handleLogin}
              className="w-full bg-blue-500 hover:bg-blue-600 text-white font-medium py-3 px-4 rounded-lg transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            >
              Sign in
            </button>

            <div className="text-xs text-gray-500">
              By signing in, you agree to The Config Team's acceptable use policy.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthProvider;
